/**
 * Truncate a text to the desired length, appending `suffix` when it is cut.
 * The returned string is at most `maxLength + suffix.length` characters long.
 */
export function truncateText(text: string, maxLength: number, suffix = '...'): string {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}${suffix}`;
}

/**
 * Capitaliza apenas a primeira letra, preservando o resto — para começo de frase.
 * Diferente de `capitalizeWords`, que normaliza cada palavra e rebaixa o restante.
 */
export function capitalizarPrimeiraLetra(valor: string): string {
  return valor.charAt(0).toUpperCase() + valor.slice(1);
}

export function capitalizeWords(value: string): string {
  return value
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * PT-BR word agreement: returns only the inflected word.
 */
export function flexionar(quantidade: number, singular: string, plural = `${singular}s`): string {
  return quantidade === 1 ? singular : plural;
}

/**
 * PT-BR word agreement prefixed by the count (`3 processos`).
 */
export function pluralizar(quantidade: number, singular: string, plural?: string): string {
  return `${quantidade} ${flexionar(quantidade, singular, plural)}`;
}

/**
 * Iniciais para avatar: primeira letra do primeiro e do último nome.
 *
 * Havia três implementações divergentes — duas pegavam primeira+última, uma pegava as
 * duas primeiras palavras, então "João Meireles de Souza" aparecia como `JM` na Visão
 * geral e `JS` na Triagem, para a mesma pessoa. Esta é a forma majoritária.
 */
export function iniciaisDoNome(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

/**
 * Rótulo de exibição para valor de enum do backend: `ALTERACAO_DADO_CADASTRO`
 * vira `alteracao dado cadastro`.
 */
export function rotularEnum(valor: string): string {
  return valor.replaceAll('_', ' ').toLowerCase().trim();
}

/**
 * Remove acentos para comparação de texto digitado. Sem isto, quem digita "joao" não encontra
 * "João" — e o operador conclui que a pessoa não existe.
 */
export function removerDiacriticos(valor: string): string {
  return valor.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}
